import CardCommon from '@op/components/CardCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import React, { useMemo, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import { ConfigProvider, Table } from 'tea-component';
import { useInfluenceListColumns, useInfluencerData } from '../../hooks';
import { NO_DATE } from '@op/constants';
import '@op/pages/Influencer/scss/index.scss';
import './index.scss';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { getTeaLocale } from '@home/utils/lang';
import BubbleCommon, {
  influencerListTipsText,
} from '@op/components/BubbleCommon';
import { arrayToObj } from '@op/utils';

const tableKey = 'author_md5';
const { pageable, autotip, sortable, scrollable } = Table.addons;
export default function InfluencerList({
  filter,
  userSynchronousValue,
  gameInfo,
  tagList,
}: any) {
  const { formatMessage } = useIntl();
  const { downloadLegacy, downloading } = useDownloadParams();
  const [tags, setTags] = useState<Array<string>>(
    tagList
      .filter((val: { no_filter: any }) => !val?.no_filter)
      .map((item: { value: any }) => item.value) || [],
  );
  /* 表格列的相关内容 */

  const params = useMemo(() => {
    return {
      kol_tags_types: tags.length === 0 ? ['-99999'] : tags,
      ...filter,
      ...userSynchronousValue,
    };
  }, [tags, filter, userSynchronousValue]);

  /* table数据的所有相关内容 */
  const {
    sort,
    getTableRecord,
    changeSortInfo,
    linkTime,
    total,
    apiError,
    pageInfo,
    changePageInfo,
    loading,
    keyword,
    setKeyword,
  } = useInfluencerData({
    params,
    tableKey,
  });

  const { tableColumns, sortKeys } = useInfluenceListColumns({
    mediaType: 'sum',
    linkTime,
    tableKey,
    sort,
    changeSortInfo,
    progressVisible: true,
    params: filter,
    tags,
    setTags,
    tagList,
    gameInfo,
  });

  const download = () => {
    const downloadLimit = 150000; // 下载限制条数
    let sortItem = sort.by;
    if (['followers_grow_more_5k', 'followers_grow'].includes(sort.by)) {
      sortItem = 'followers';
    }
    downloadLegacy({
      limit: downloadLimit,
      params: {
        ...params,
        search_keyword: keyword,
        page: 1,
        page_size: 1000,
        sort_item: sortItem,
        sort_model: sort.order,
        extraParams: {
          tagList: arrayToObj(tagList, 'key'),
          gameInfo: {
            edition_unified_id: gameInfo?.edition_unified_id,
          },
        },
      },
      downloadFunc: 'getInfluencerGenreCreatorList',
      sheetName: 'Genre Creator List',
      total,
      simpleName: 'Genre Creator List',
    });
  };

  const locale = getTeaLocale(getLocale());

  return (
    <CardCommon
      className="influencer-list-wrapper"
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {formatMessage({ id: 'Influencer List' })}
          <BubbleCommon
            placement="top"
            tipsMap={influencerListTipsText}
          ></BubbleCommon>
        </div>
      }
      operations={[
        {
          element: (
            <FilterBoxCommon
              className="influencer-list-filter"
              downloadLoading={downloading}
              left={[]}
              right={[
                {
                  key: 'search',
                  className: 'search-input-box',
                  defaultParams: {
                    placeholder: formatMessage({
                      id: 'Search influencer name',
                    }),
                    onSearch: (value: string) => setKeyword(value),
                    onClear: () => setKeyword(''),
                  },
                  defaultValue: keyword,
                },
                {
                  key: 'download',
                  className: 'influencer-list-download',
                  event: {
                    onClick: download,
                  },
                },
              ]}
              onChange={() => {}}
            ></FilterBoxCommon>
          ),
        },
      ]}
    >
      <CardCommon
        loadingHeight={350}
        loading={loading}
        className="influencer-table-wrapper"
        contentCard
      >
        <ConfigProvider locale={locale}>
          <Table
            className="dbo-table-sticky dbo-table-sticky--withfilter dbo-influencer-table dbo-influencer-table--selectable"
            bordered="all"
            records={getTableRecord()}
            recordKey={(item) =>
              item.author_md5 + item.author_name + item.channel_name
            }
            columns={[
              {
                key: 'Index',
                header: '#',
                width: 92,
                fixed: 'left',
                render: (_item, _key, index) => {
                  return (pageInfo.page - 1) * pageInfo.page_size + index + 1;
                },
              },
              ...tableColumns,
            ]}
            addons={[
              pageable({
                recordCount: total,
                pageIndex: pageInfo.page,
                pageSize: pageInfo.page_size,
                onPagingChange: ({ pageSize, pageIndex }) =>
                  changePageInfo(pageIndex, pageSize),
              }),
              autotip({
                isLoading: loading,
                emptyText: formatMessage({ id: NO_DATE.SELECTION }),
              }),
              sortable({
                columns: sortKeys.map((key) => ({
                  key,
                  prefer: 'desc',
                })),
                value: [sort],
                onChange: (_value, { sort }) => {
                  changeSortInfo(sort);
                },
              }),
              scrollable({
                minWidth: 'auto',
              }),
            ]}
          />
        </ConfigProvider>
      </CardCommon>
    </CardCommon>
  );
}
